import type { GetFleetsResponse } from '@/types/fleet'
import type { Options } from 'ky'
import type { AdhocBookingBookedTransportation } from '@/types/booking'
import { httpClient } from '@/utils/http'
import {
  BOOKED_TRANSPORTATION_STEPS,
  BOOKING_TRIP_DIRECTION,
  BOOKING_TRIP_TYPE,
} from '@/utils/constant'

export const fleetService = {
  fleets: (options?: Options) =>
    httpClient.get('fleets', options).json<GetFleetsResponse>(),
  fillBooking: async (
    params: AdhocBookingBookedTransportation,
    options?: Options,
  ) => {
    try {
      const { stepService } = await import('@/services/step')
      const { bookingService } = await import('@/services/booking')
      const { draw, random } = await import('radash')

      // TODO: Step 1. Trip type
      const passengerQuantity = random(
        5,
        params.adhoc_transportation_type.capacity,
      )
      const tripType = draw(Object.values(BOOKING_TRIP_TYPE))
      const pointToPointType = draw(Object.values(BOOKING_TRIP_DIRECTION))
      await httpClient.patch(`bookings/${params.id}/trip_type`, {
        ...options,
        json: {
          adhoc_booking_booked_transportation: {
            passenger_quantity: passengerQuantity,
            trip_type: tripType,
            ...(tripType === BOOKING_TRIP_TYPE.POINT_TO_POINT
              ? { point_to_point_type: pointToPointType }
              : {}),
          },
        },
      })
      await stepService.update({
        id: params.id,
        step: BOOKED_TRANSPORTATION_STEPS.STEP_TRIP_TYPE,
        type: 'booked_transportation',
      })
      // TODO: Step 2. Select Date
      // TODO: Step 2.1. Show available Date
      const resDates = await httpClient
        .get(`bookings/${params.id}/dates`, options)
        .json<any>()
      const selectedDate = resDates.data.find(
        (item: any) =>
          Object.entries(item)
            .at(0)
            // @ts-expect-error
            ?.at(draw([-1, -2, -3]))?.available,
      )
      if (!selectedDate) return Promise.reject('No available date')
      const tripDate = Object.keys(selectedDate).at(0)
      // TODO: Step 2.2. Select available Date
      await httpClient.patch(`bookings/${params.id}/dates/${tripDate}/select`)
      await stepService.update({
        id: params.id,
        step: BOOKED_TRANSPORTATION_STEPS.STEP_DATE,
        type: 'booked_transportation',
      })
      // TODO: Step 3. Select Time
      const resTimes = await httpClient
        .get(`bookings/${params.id}/times`)
        .json<any>()
      const selectedTimes = draw(Object.entries(resTimes.data))
      if (!selectedTimes) return Promise.reject('No available time')
      const { addTime } = await import('@/utils/time')
      // TODO: Step 3.1. Hourly disposal
      const pathBookingTimeParams = new URLSearchParams()
      const [selectedTime] = selectedTimes
      const toAddMinute = random(5, 15)
      const departTime = addTime(selectedTime, { minutes: toAddMinute })
      if (tripType === BOOKING_TRIP_TYPE.HOURLY_RENTAL) {
        pathBookingTimeParams.set('depart_time', departTime)
        pathBookingTimeParams.set('duration', toAddMinute.toString())
      }
      // TODO: Step 3.2. Point to point
      if (tripType === BOOKING_TRIP_TYPE.POINT_TO_POINT) {
        // TODO: Step 3.2.1 Point to point one way
        if (pointToPointType === BOOKING_TRIP_DIRECTION.ONE_WAY) {
          pathBookingTimeParams.set('depart_time', departTime)
        }
        // TODO: Step 3.2.2 Point to point two way
        if (pointToPointType === BOOKING_TRIP_DIRECTION.TWO_WAY) {
          const toAddHour = random(1, 3)
          pathBookingTimeParams.set('depart_time', departTime)
          pathBookingTimeParams.set(
            'return_time',
            addTime(selectedTime, { hours: toAddHour }),
          )
        }
      }
      await httpClient.patch(
        `bookings/${params.id}/times/${selectedTime}/select`,
        {
          ...options,
          searchParams: pathBookingTimeParams,
        },
      )
      await stepService.update({
        id: params.id,
        step: BOOKED_TRANSPORTATION_STEPS.STEP_TIME,
        type: 'booked_transportation',
      })
      // TODO: Step 4. Select Routes
      const resUniversalStudioPlaces = await httpClient
        .get('places', {
          searchParams: {
            search: 'Universal Studios',
          },
        })
        .json<any>()
      const resAirportPlaces = await httpClient
        .get('places', {
          searchParams: {
            search: 'Singapore Changi Airport',
          },
        })
        .json<any>()
      const pickupRoute = resAirportPlaces.data[0]
      // TODO: Step 4.1. Select Routes - Hourly
      if (tripType === BOOKING_TRIP_TYPE.HOURLY_RENTAL) {
        const dropOffRoute = resUniversalStudioPlaces.data[0]
        await httpClient.patch(`bookings/${params.id}/hourly/routes`, {
          json: {
            booked_transportation_hourly: {
              pickup_address: pickupRoute.name,
              drop_off_address: dropOffRoute.name,
              itinerary:
                'Wait my friend 15 minutes before we go in Bleecker Street',
            },
          },
        })
      }
      // TODO: Step 4.2. Select Routes - Point to point
      if (tripType === BOOKING_TRIP_TYPE.POINT_TO_POINT) {
        const resTransportationBooking = await bookingService.transportation(
          params.id,
        )
        // TODO: Step 4.2.1 Select Routes - Point to point - One way
        if (pointToPointType === BOOKING_TRIP_DIRECTION.ONE_WAY) {
          const departPoint =
            resTransportationBooking.data.booked_transportation
              .booked_transportation_point_to_points &&
            resTransportationBooking.data.booked_transportation.booked_transportation_point_to_points.find(
              (p) => p.category === 'depart_trip',
            )
          if (departPoint) {
            await httpClient.patch(`bookings/${params.id}/points/routes`, {
              json: {
                adhoc_booking_booked_transportation: {
                  booked_transportation_point_to_points_attributes: [
                    {
                      id: departPoint.id,
                      pickup_address: pickupRoute.address,
                      remarks: 'Wait my friend 30 minutes before we go!',
                      adhoc_booking_stop_points_attributes:
                        resUniversalStudioPlaces.data.map((place) => ({
                          address: place.name,
                        })),
                    },
                  ],
                },
              },
            })
          }
        }
        // TODO: Step 4.2.1 Select Routes - Point to point - Two way
        if (pointToPointType === BOOKING_TRIP_DIRECTION.TWO_WAY) {
          await httpClient.patch(`bookings/${params.id}/points/routes`, {
            json: {
              adhoc_booking_booked_transportation: {
                booked_transportation_point_to_points_attributes:
                  resTransportationBooking.data.booked_transportation
                    .booked_transportation_point_to_points &&
                  resTransportationBooking.data.booked_transportation.booked_transportation_point_to_points.map(
                    (p) => ({
                      id: p.id,
                      pickup_address: pickupRoute.address,
                      remarks: 'Wait my friend 30 minutes before we go!',
                      adhoc_booking_stop_points_attributes:
                        resUniversalStudioPlaces.data.map((place) => ({
                          address: place.name,
                        })),
                    }),
                  ),
              },
            },
          })
        }
      }
      await stepService.update({
        id: params.id,
        step: BOOKED_TRANSPORTATION_STEPS.STEP_ROUTES,
        type: 'booked_transportation',
      })
      // TODO: Step 5. Select adds-on
      const resAddons = await httpClient
        .get('add_ons', {
          searchParams: {
            page: 1,
            per_page: 10,
            booking_id: params.id,
          },
        })
        .json<any>()
      const selectedAddon = resAddons.data.products.find(
        (p: any) => p.product_type === 'quantity',
      )
      if (selectedAddon) {
        const resTransportationBooking = await bookingService.transportation(
          params.id,
        )
        if (
          tripType === BOOKING_TRIP_TYPE.HOURLY_RENTAL &&
          resTransportationBooking.data.booked_transportation
            ?.booked_transportation_hourly?.id
        ) {
          const existingAddOn =
            resTransportationBooking.data.booked_transportation?.booked_transportation_hourly?.adhoc_booking_booked_products?.at(
              0,
            ) ?? null
          await httpClient.patch(
            `add_ons/update/${params.id}/hourly/${
              resTransportationBooking.data.booked_transportation
                .booked_transportation_hourly.id
            }`,
            {
              json: {
                booked_transportation_hourly: {
                  adhoc_booking_booked_products_attributes: [
                    {
                      ...(existingAddOn ? { id: existingAddOn.id } : {}),
                      objectable_id:
                        resTransportationBooking.data.booked_transportation
                          .booked_transportation_hourly.id,
                      objectable_type: 'BookedTransportation::Hourly',
                      quantity:
                        resTransportationBooking.data.booked_transportation
                          .passenger_quantity,
                      product_id: selectedAddon.id,
                    },
                  ],
                },
              },
            },
          )
        }
        if (
          tripType === BOOKING_TRIP_TYPE.POINT_TO_POINT &&
          resTransportationBooking.data.booked_transportation
            ?.booked_transportation_point_to_points?.length
        ) {
          await httpClient.patch(`add_ons/update/${params.id}`, {
            json: {
              adhoc_booking_booked_transportation: {
                booked_transportation_point_to_points_attributes:
                  resTransportationBooking.data.booked_transportation.booked_transportation_point_to_points.map(
                    (point) => ({
                      id: point.id,
                      adhoc_booking_booked_products_attributes: [
                        {
                          ...(point.adhoc_booking_booked_products?.at(0)?.id
                            ? {
                                id: point.adhoc_booking_booked_products.at(0)
                                  ?.id,
                              }
                            : {}),
                          objectable_id: point.id,
                          objectable_type: 'BookedTransportation::PointToPoint',
                          quantity:
                            resTransportationBooking.data.booked_transportation
                              .passenger_quantity,
                          product_id: selectedAddon.id,
                        },
                      ],
                    }),
                  ),
              },
            },
          })
        }
        await stepService.update({
          id: params.id,
          step: BOOKED_TRANSPORTATION_STEPS.STEP_ADD_ONS,
          type: 'booked_transportation',
        })
      }
      await stepService.update({
        id: params.id,
        step: BOOKED_TRANSPORTATION_STEPS.STEP_COMPLETED,
        type: 'booked_transportation',
      })
    } catch (error) {
      console.error(error)
      return Promise.reject(error)
    }
  },
}
